import { useEffect, useState } from "react";
import AppLayout from "@/components/layout/AppLayout";
import { apiPrivate } from "@/lib/api";
import { Plus, Edit2, Trash2, X, Loader2 } from "lucide-react";

const CATEGORIES = [
    { k: "quote", label: "Quote" },
    { k: "assemblee", label: "Assemblee" },
    { k: "fiscalita", label: "Fiscalità" },
    { k: "piattaforma", label: "Piattaforma" },
    { k: "visura", label: "Visura" },
    { k: "altro", label: "Altro" },
];

export default function AdminFaq() {
    const [faqs, setFaqs] = useState([]);
    const [editing, setEditing] = useState(null);
    const [creating, setCreating] = useState(false);

    const load = () => apiPrivate.get("/admin/faq").then(r => setFaqs(r.data)).catch(() => {});
    useEffect(() => { load(); }, []);

    const remove = async (id) => {
        if (!window.confirm("Eliminare questa FAQ?")) return;
        await apiPrivate.delete(`/admin/faq/${id}`);
        load();
    };

    return (
        <AppLayout admin>
            <div className="max-w-[1400px] mx-auto" data-testid="admin-faq">
                <div className="flex items-end justify-between gap-4 flex-wrap">
                    <div>
                        <span className="label-tag text-magenta">FAQ</span>
                        <h1 className="font-display text-5xl mt-3 tracking-tighter">Gestione <span className="font-serif-italic text-acid">FAQ</span>.</h1>
                    </div>
                    <button onClick={() => setCreating(true)} className="btn-rebel" data-testid="btn-new-faq"><Plus className="h-4 w-4" /> Nuova FAQ</button>
                </div>

                <div className="mt-8 space-y-2" data-testid="admin-faq-list">
                    {faqs.length === 0 && <div className="bento p-10 text-center text-zinc-500" data-testid="faq-empty-admin">Nessuna FAQ.</div>}
                    {faqs.map(f => (
                        <div key={f.id} className="bento p-5" data-testid={`admin-faq-${f.id}`}>
                            <div className="flex items-start justify-between gap-4">
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-3 flex-wrap">
                                        <span className="font-grotesk font-medium">{f.question}</span>
                                        <span className="label-tag">{f.category}</span>
                                        {!f.published && <span className="bg-zinc-800 text-zinc-400 text-[10px] font-bold px-2 py-0.5">BOZZA</span>}
                                    </div>
                                    <p className="text-zinc-400 text-sm mt-2 line-clamp-2">{f.answer}</p>
                                    <div className="mt-3 text-xs text-zinc-500 flex gap-4 font-grotesk uppercase tracking-widest">
                                        <span>👍 {f.helpful}</span>
                                        <span>👎 {f.not_helpful}</span>
                                        <span>Ordine: {f.order}</span>
                                    </div>
                                </div>
                                <div className="flex gap-2">
                                    <button onClick={() => setEditing(f)} className="p-2 border border-zinc-800 hover:border-acid hover:text-acid transition" data-testid={`edit-faq-${f.id}`}><Edit2 className="h-4 w-4" /></button>
                                    <button onClick={() => remove(f.id)} className="p-2 border border-zinc-800 hover:border-magenta hover:text-magenta transition" data-testid={`delete-faq-${f.id}`}><Trash2 className="h-4 w-4" /></button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {(editing || creating) && (
                    <FaqModal
                        initial={editing}
                        onClose={() => { setEditing(null); setCreating(false); }}
                        onSaved={() => { setEditing(null); setCreating(false); load(); }}
                    />
                )}
            </div>
        </AppLayout>
    );
}

function FaqModal({ initial, onClose, onSaved }) {
    const [form, setForm] = useState(initial || { question: "", answer: "", category: "altro", published: true, order: 0 });
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState(null);

    const submit = async () => {
        setSaving(true); setErr(null);
        try {
            if (initial) {
                await apiPrivate.patch(`/admin/faq/${initial.id}`, form);
            } else {
                await apiPrivate.post("/admin/faq", form);
            }
            onSaved();
        } catch (e) { setErr(e?.response?.data?.detail || "Errore"); }
        finally { setSaving(false); }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={onClose} data-testid="faq-modal">
            <div className="bg-[#0a0a0a] border border-zinc-800 max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                <div className="p-8">
                    <div className="flex justify-between items-start">
                        <div>
                            <span className="label-tag">— {initial ? "Modifica" : "Nuova"} FAQ</span>
                            <h2 className="font-display text-3xl tracking-tighter mt-2">{initial ? "Aggiorna risposta." : "Crea risposta."}</h2>
                        </div>
                        <button onClick={onClose} data-testid="faq-modal-close"><X className="h-5 w-5 text-zinc-500" /></button>
                    </div>
                    <div className="mt-6 space-y-4">
                        <div>
                            <div className="label-tag mb-2">Domanda *</div>
                            <input value={form.question} onChange={(e) => setForm(s => ({ ...s, question: e.target.value }))} data-testid="faq-question" className="w-full bg-[#0F0F0F] border border-zinc-800 text-white px-3 py-2 font-grotesk focus:outline-none focus:border-acid" />
                        </div>
                        <div>
                            <div className="label-tag mb-2">Risposta *</div>
                            <textarea rows={8} value={form.answer} onChange={(e) => setForm(s => ({ ...s, answer: e.target.value }))} data-testid="faq-answer" className="w-full bg-[#0F0F0F] border border-zinc-800 text-white p-3 font-grotesk focus:outline-none focus:border-acid resize-none" />
                        </div>
                        <div className="grid grid-cols-3 gap-4">
                            <div>
                                <div className="label-tag mb-2">Categoria</div>
                                <select value={form.category} onChange={(e) => setForm(s => ({ ...s, category: e.target.value }))} data-testid="faq-category-sel" className="w-full bg-[#0F0F0F] border border-zinc-800 text-white px-3 py-2 font-grotesk focus:outline-none">
                                    {CATEGORIES.map(c => <option key={c.k} value={c.k}>{c.label}</option>)}
                                </select>
                            </div>
                            <div>
                                <div className="label-tag mb-2">Ordine</div>
                                <input type="number" value={form.order} onChange={(e) => setForm(s => ({ ...s, order: parseInt(e.target.value) || 0 }))} data-testid="faq-order" className="w-full bg-[#0F0F0F] border border-zinc-800 text-white px-3 py-2 font-grotesk focus:outline-none" />
                            </div>
                            <div>
                                <div className="label-tag mb-2">Stato</div>
                                <select value={form.published ? "1" : "0"} onChange={(e) => setForm(s => ({ ...s, published: e.target.value === "1" }))} data-testid="faq-published" className="w-full bg-[#0F0F0F] border border-zinc-800 text-white px-3 py-2 font-grotesk focus:outline-none">
                                    <option value="1">Pubblicata</option>
                                    <option value="0">Bozza</option>
                                </select>
                            </div>
                        </div>
                        {err && <div className="border border-magenta/40 bg-magenta/10 text-magenta px-3 py-2 text-sm" data-testid="faq-err">{err}</div>}
                        <button onClick={submit} disabled={saving || !form.question || !form.answer} className="btn-rebel w-full justify-center" data-testid="faq-save">
                            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (initial ? "Aggiorna FAQ" : "Pubblica FAQ")}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
